import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import type { GenerationCard } from '../types/generationCard'
import { deleteSourceImage, deleteSourceImages } from '../utils/sourceImageDb'

const STORAGE_KEY = 'recent_generations'
// 30, а не 10: студия /generate сделала историю рабочим пространством —
// два стола питаются отсюда, и десяток «съедался» за одну сессию подбора.
// Записи лёгкие (URL + метаданные, base64 не храним), квоте не угрожают.
const MAX_ITEMS = 30

interface RecentGenerationsContextValue {
	recent: GenerationCard[]
	addGeneration: (gen: Omit<GenerationCard, 'id' | 'createdAt' | 'type'> & { id?: string }) => void
	removeGeneration: (id: string) => void
	/** Точечное обновление записи (оценка 👍/👎 с карточки) — персист сразу. */
	updateGeneration: (id: string, patch: Partial<GenerationCard>) => void
	pruneIfMissing: (id: string) => void
}

const RecentGenerationsContext = createContext<RecentGenerationsContextValue>({
	recent: [],
	addGeneration: () => {},
	removeGeneration: () => {},
	updateGeneration: () => {},
	pruneIfMissing: () => {},
})

function load(): GenerationCard[] {
	try {
		const raw = localStorage.getItem(STORAGE_KEY)
		if (!raw) return []
		const items = JSON.parse(raw) as (GenerationCard & { resultUrl?: string })[]
		// Нормализуем старый формат и чистим протухшие presigned URL
		return items.map((item) => {
			let url = item.previewUrl || item.resultUrl || ''
			try {
				const u = new URL(url)
				if (u.searchParams.has('X-Amz-Signature')) {
					url = u.origin + u.pathname
				}
			} catch { /* оставляем как есть */ }
			// Автомиграция: если нет kind — вычисляем из isVideo
			const kind = item.kind || (item.isVideo ? 'video' : 'image')
			return { ...item, type: 'generation' as const, previewUrl: url, kind }
		})
	} catch {
		return []
	}
}

function save(items: GenerationCard[]): void {
	// Новые записи не хранят base64 sourceImageUrl — только sourceImageDbKey.
	// Для старых записей (legacy base64) оставляем QuotaExceeded-fallback.
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
	} catch {
		// QuotaExceededError — удаляем legacy base64 у старых записей
		const trimmed = items.map((item) => ({ ...item }))
		for (let i = trimmed.length - 1; i >= 0; i--) {
			if (trimmed[i].sourceImageUrl) {
				trimmed[i] = { ...trimmed[i], sourceImageUrl: undefined }
				try {
					localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed))
					return
				} catch {
					// продолжаем удалять
				}
			}
		}
		try {
			localStorage.setItem(
				STORAGE_KEY,
				JSON.stringify(trimmed.map((item) => ({ ...item, sourceImageUrl: undefined }))),
			)
		} catch {
			// совсем не влезает — не сохраняем
		}
	}
}

export function RecentGenerationsProvider({ children }: { children: React.ReactNode }) {
	const [recent, setRecent] = useState<GenerationCard[]>(load)

	useEffect(() => {
		save(recent)
	}, [recent])

	const addGeneration = useCallback(
		(gen: Omit<GenerationCard, 'id' | 'createdAt' | 'type'> & { id?: string }) => {
			const entry: GenerationCard = {
				...gen,
				type: 'generation',
				id: gen.id ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`,
				createdAt: Date.now(),
			}
			setRecent((prev) => {
				if (prev.some((g) => g.id === entry.id || g.previewUrl === entry.previewUrl)) return prev
				const next = [entry, ...prev]
				// Удаляем IndexedDB-записи у вытесненных элементов
				const pruned = next.slice(MAX_ITEMS)
				const keysToDelete = pruned
					.map((g) => g.sourceImageDbKey)
					.filter((k): k is string => !!k)
				if (keysToDelete.length > 0) deleteSourceImages(keysToDelete)
				return next.slice(0, MAX_ITEMS)
			})
		},
		[],
	)

	const removeGeneration = useCallback((id: string) => {
		setRecent((prev) => {
			const item = prev.find((g) => g.id === id)
			// Удаляем исходник из IndexedDB
			if (item?.sourceImageDbKey) deleteSourceImage(item.sourceImageDbKey)
			return prev.filter((g) => g.id !== id)
		})
	}, [])

	const updateGeneration = useCallback((id: string, patch: Partial<GenerationCard>) => {
		setRecent((prev) => prev.map((g) => (g.id === id ? { ...g, ...patch } : g)))
	}, [])

	const pruneIfMissing = useCallback((id: string) => {
		const item = recent.find((g) => g.id === id)
		const url = item?.previewUrl
		if (!url || url.startsWith('data:') || url.startsWith('blob:')) return
		fetch(url, { method: 'HEAD', cache: 'no-store' })
			.then((res) => {
				if (res.status === 404) removeGeneration(id)
			})
			.catch(() => {})
	}, [recent, removeGeneration])

	return (
		<RecentGenerationsContext.Provider value={{ recent, addGeneration, removeGeneration, updateGeneration, pruneIfMissing }}>
			{children}
		</RecentGenerationsContext.Provider>
	)
}

// eslint-disable-next-line react-refresh/only-export-components
export function useRecentGenerationsContext() {
	return useContext(RecentGenerationsContext)
}
